import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';
import toast from 'react-hot-toast';

import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useId } from '@floating-ui/react';

import { SaleBidStructure } from '__generated__/globalTypes';
import { Button, Checkbox, RadioButton, RadioGroup } from 'ui-kit';

import { parseCurrencyToCents } from 'configs/columns';

import {
  SaveSaleBidDraft,
  SaveSaleBidDraftVariables,
} from 'mutation/__generated__/saveSaleBidDraft';
import { SubmitSaleBid, SubmitSaleBidVariables } from 'mutation/__generated__/submitSaleBid';
import { SAVE_SALE_BID_DRAFT } from 'mutation/saveSaleBidDraft';
import { SUBMIT_SALE_BID } from 'mutation/submitSaleBid';

import { SALE_BID_DRAFTS_QUERY, SALE_BIDDER_NAMES_QUERY, SALE_BIDS_QUERY } from 'query/sales';
import { SaleBidDraftsQuery, SaleBidderNames, SaleBidsQuery } from 'query/__generated__/sales';

import { firstGraphQLError } from 'utils/apolloGraphQLError';

import { SaleBidConfirmDialog } from './SaleBidConfirmDialog';
import { labelledPools, SaleBidsTable, SaleBidsTableBid } from './SaleBidsTable';
import {
  BidPrice,
  BidTerms,
  bookValueCents,
  formatPctE4,
  parsePercentToPctE4,
  priceEquivalentLabel,
  priceLabel,
  SaleOffering,
  seriesKey,
} from './saleBids';
import { formatCentralClockTime, formatCentralTime } from './salesUtils';
import { PRIMARY_BUTTON_LIGHT_THEME } from './styles';

type SaleBid = SaleBidsQuery['saleBids'][number];
type SaleBidDraft = SaleBidDraftsQuery['saleBidDrafts'][number];

export const ALREADY_BID_COPY =
  "You've already submitted a final bid on these pools. Bids can't be changed or withdrawn.";

// api classifies the "no round has unsealed yet" refusal distinctly from a real failure
// (Authorization, NotFound, network, timeout...); matching on that classification, rather than
// just "there was an error," keeps a real failure from being told to the seller as "sealed."
const isSealedError = (error: ApolloError | undefined): boolean =>
  firstGraphQLError(error)?.extensions?.errorClass === 'Sealed';

const isAlreadyBidError = (err: unknown): boolean =>
  firstGraphQLError(err)?.extensions?.code === 'ALREADY_BID';

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback;

type BidPriceMode = 'PCT' | 'AMOUNT';

const parsePrice = (mode: BidPriceMode, rawValue: string): BidPrice | undefined => {
  if (mode === 'PCT') {
    const pctE4 = parsePercentToPctE4(rawValue);
    return pctE4 == null ? undefined : { pct_e4: pctE4 };
  }
  const amountCents = parseCurrencyToCents(rawValue);
  return amountCents == null || amountCents <= 0 ? undefined : { amount_cents: amountCents };
};

const toBidInput = (saleId: string, terms: BidTerms) => ({
  sale_id: saleId,
  round: terms.round,
  structure: terms.structure,
  offering_ids: terms.offering_ids,
  pct_e4: terms.pct_e4,
  amount_cents: terms.amount_cents,
});

const termsKey = (terms: BidTerms): string =>
  `${seriesKey(terms.round, terms.structure, terms.offering_ids)}|${priceLabel(terms)}`;

const toTableBid = (bid: SaleBid, offerings: SaleOffering[]): SaleBidsTableBid => ({
  ...bid,
  pools: labelledPools(bid, offerings),
});

// A bidder whose name didn't load is shown by its company ID rather than left blank, under a
// notice that the names failed.
const bidderName = (bidders: SaleBidderNames['saleBidders'], companyId: string): string =>
  bidders.find((bidder) => bidder.buyer_company_id === companyId)?.company_name ?? companyId;

// The seller's own bids read (unsealed rounds only) has no submission form -- sellers don't bid
// on their own sale. Every row and amount comes from saleBids' answer alone.
const SealedBidsList: FC<{ saleId: string; offerings: SaleOffering[] }> = ({
  saleId,
  offerings,
}) => {
  const { data, error, loading } = useQuery<SaleBidsQuery>(SALE_BIDS_QUERY, {
    variables: { saleId },
    errorPolicy: 'all',
  });
  // no-cache: the home page's blotter reads saleBidders with another selection, and Apollo would
  // otherwise overwrite one list with the other's rows.
  const bidderNames = useQuery<SaleBidderNames>(SALE_BIDDER_NAMES_QUERY, {
    variables: { saleId },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  if (loading || (bidderNames.loading && !bidderNames.data)) {
    return <p className="text-sm text-gray-400 light:text-gray-700">Loading bids…</p>;
  }
  if (isSealedError(error)) {
    return (
      <p
        data-testid="sale-seller-bids-sealed"
        className="text-sm text-gray-400 light:text-gray-700"
      >
        Bids are sealed until this round closes.
      </p>
    );
  }
  if (error) {
    return <p className="text-sm text-status-error light:text-error-muted">Failed to load bids.</p>;
  }
  const bids = data?.saleBids ?? [];
  if (bids.length === 0) {
    return (
      <p data-testid="sale-seller-bids-empty" className="text-sm text-gray-400 light:text-gray-700">
        No bids yet.
      </p>
    );
  }
  const bidders = bidderNames.data?.saleBidders ?? [];
  return (
    <>
      {bidderNames.error && (
        <p
          data-testid="sale-seller-bidder-names-failed"
          className="text-sm text-status-error light:text-error-muted"
        >
          Couldn't load the bidders' company names, so each bidder is shown by its company ID.
        </p>
      )}
      <SaleBidsTable
        saleId={saleId}
        bids={bids.map((bid) => ({
          ...toTableBid(bid, offerings),
          bidder_name: bidderName(bidders, bid.buyer_company_id),
        }))}
        linkPools
        showBidder
        tableTestId="sale-seller-bids"
        rowTestId="sale-seller-bid"
      />
    </>
  );
};

const AUTOSAVE_DELAY_MS = 1000;

const DEADLINE_PASSED_COPY = "This round's bidding deadline has passed.";

// Each status belongs to the terms it was written for, and shows only while the form holds them.
type DraftStatus =
  | { kind: 'idle' }
  | { kind: 'saving'; termsKey: string }
  | { kind: 'saved'; termsKey: string; savedAt: string }
  | { kind: 'failed'; termsKey: string; message: string }
  | { kind: 'alreadyFinal'; termsKey: string };

const IDLE: DraftStatus = { kind: 'idle' };

const draftTerms = (draft: SaleBidDraft): BidTerms => ({
  round: draft.round,
  structure: draft.structure,
  offering_ids: [...draft.offering_ids],
  ...(draft.pct_e4 != null
    ? { pct_e4: draft.pct_e4 }
    : { amount_cents: Number(draft.amount_cents) }),
});

const DraftStatusLine: FC<{ status: DraftStatus }> = ({ status }) => (
  <p
    data-testid="sale-bid-draft-status"
    aria-live="polite"
    className={`text-xs ${
      status.kind === 'failed' || status.kind === 'alreadyFinal'
        ? 'text-status-error light:text-error-muted'
        : 'text-gray-400 light:text-gray-700'
    }`}
  >
    {status.kind === 'saving' && 'Saving…'}
    {status.kind === 'saved' && `Draft saved · ${formatCentralClockTime(status.savedAt)}`}
    {status.kind === 'failed' && `Draft not saved: ${status.message}`}
    {status.kind === 'alreadyFinal' && `Draft not saved: ${ALREADY_BID_COPY}`}
  </p>
);

const BidForm: FC<{
  saleId: string;
  round: number;
  deadlineAt: string;
  calculationDate: string;
  offerings: SaleOffering[];
  existingBids: SaleBid[];
  saleAllowsJointVentureBids: boolean;
  // Resolves with the refetched bids, or undefined when the refetch failed.
  onBidsChanged: () => Promise<SaleBid[] | undefined>;
}> = ({
  saleId,
  round,
  deadlineAt,
  calculationDate,
  offerings,
  existingBids,
  saleAllowsJointVentureBids,
  onBidsChanged,
}) => {
  const [selectedOfferingIds, setSelectedOfferingIds] = useState<string[]>([]);
  const [structure, setStructure] = useState<SaleBidStructure>(SaleBidStructure.CASH);
  const [mode, setMode] = useState<BidPriceMode>('PCT');
  const [rawValue, setRawValue] = useState('');
  const [confirmTerms, setConfirmTerms] = useState<BidTerms>();
  // From Submit until the refetched bids say whether the series is final.
  const [finalizing, setFinalizing] = useState(false);
  // Series this form has seen go final, so it stays locked even if the bids refetch fails.
  const [knownFinalSeries, setKnownFinalSeries] = useState<string[]>([]);
  const [draftStatus, setDraftStatus] = useState<DraftStatus>(IDLE);
  const [jointVentureDraftRestoredAsCash, setJointVentureDraftRestoredAsCash] = useState(false);
  const amountInputId = useId();

  const bidderHasEdited = useRef(false);
  const restoreAttempted = useRef(false);
  const lastSavedTermsKey = useRef<string>();
  const pendingDraft = useRef<BidTerms>();
  const draftWriteInFlight = useRef(false);
  const draftQueuedBehindWrite = useRef<BidTerms>();
  const autosaveKeyNow = useRef<string>();
  const mounted = useRef(true);

  // network-only: the restore must read what the server holds, not this tab's cache.
  const drafts = useQuery<SaleBidDraftsQuery>(SALE_BID_DRAFTS_QUERY, {
    variables: { saleId },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });
  const [saveDraft] = useMutation<SaveSaleBidDraft, SaveSaleBidDraftVariables>(SAVE_SALE_BID_DRAFT);
  const draftsSettled = drafts.data != null || drafts.error != null;
  const [submitBid, { loading: submitting }] = useMutation<SubmitSaleBid, SubmitSaleBidVariables>(
    SUBMIT_SALE_BID,
  );

  const finalSeriesKeys = useMemo(
    () =>
      new Set([
        ...existingBids.map((bid) => seriesKey(bid.round, bid.structure, bid.offering_ids)),
        ...knownFinalSeries,
      ]),
    [existingBids, knownFinalSeries],
  );
  const isSeriesFinal = (terms: Pick<BidTerms, 'round' | 'structure' | 'offering_ids'>) =>
    finalSeriesKeys.has(seriesKey(terms.round, terms.structure, terms.offering_ids));

  const price = parsePrice(mode, rawValue);
  const currentTerms: BidTerms | undefined =
    price && selectedOfferingIds.length > 0
      ? { ...price, round, structure, offering_ids: selectedOfferingIds }
      : undefined;
  const priceEquivalent = currentTerms
    ? priceEquivalentLabel(
        currentTerms,
        bookValueCents(
          offerings.filter((offering) => selectedOfferingIds.includes(offering.offering_id)),
        ),
        calculationDate,
      )
    : undefined;

  const submittedBid = useMemo(() => {
    if (selectedOfferingIds.length === 0) {
      return undefined;
    }
    const key = seriesKey(round, structure, selectedOfferingIds);
    return existingBids.find(
      (bid) => seriesKey(bid.round, bid.structure, bid.offering_ids) === key,
    );
  }, [existingBids, round, structure, selectedOfferingIds]);
  const isFinal =
    selectedOfferingIds.length > 0 &&
    isSeriesFinal({ round, structure, offering_ids: selectedOfferingIds });

  const autosaveTerms =
    currentTerms && !isFinal && !submitting && !finalizing && confirmTerms == null
      ? currentTerms
      : undefined;
  const autosaveKey = autosaveTerms ? termsKey(autosaveTerms) : undefined;
  autosaveKeyNow.current = autosaveKey;

  const markSeriesFinal = (terms: BidTerms) =>
    setKnownFinalSeries((current) => [
      ...current,
      seriesKey(terms.round, terms.structure, terms.offering_ids),
    ]);

  const markEdited = () => {
    bidderHasEdited.current = true;
  };

  const toggleOffering = (offeringId: string) => {
    markEdited();
    setSelectedOfferingIds((current) =>
      current.includes(offeringId)
        ? current.filter((id) => id !== offeringId)
        : [...current, offeringId],
    );
  };

  const isStillToAutosave = (terms: BidTerms) => termsKey(terms) === autosaveKeyNow.current;

  // One write at a time, so loan-service keeps the latest terms: a change made while a write is in
  // flight waits behind it, replacing any change already waiting.
  const writeDraft = async (terms: BidTerms) => {
    if (draftWriteInFlight.current) {
      draftQueuedBehindWrite.current = terms;
      return;
    }
    const key = termsKey(terms);
    const show = (status: DraftStatus) => {
      if (mounted.current) {
        setDraftStatus(status);
      }
    };
    if (DateTime.fromISO(deadlineAt) <= DateTime.now()) {
      show({ kind: 'failed', termsKey: key, message: DEADLINE_PASSED_COPY });
      return;
    }
    draftWriteInFlight.current = true;
    lastSavedTermsKey.current = key;
    show({ kind: 'saving', termsKey: key });
    try {
      const { data } = await saveDraft({ variables: { input: toBidInput(saleId, terms) } });
      if (data?.saveSaleBidDraft) {
        show({ kind: 'saved', termsKey: key, savedAt: data.saveSaleBidDraft.saved_at });
      }
    } catch (mutationError) {
      if (lastSavedTermsKey.current === key) {
        lastSavedTermsKey.current = undefined;
      }
      if (isAlreadyBidError(mutationError)) {
        if (mounted.current) {
          markSeriesFinal(terms);
        }
        show({ kind: 'alreadyFinal', termsKey: key });
        void onBidsChanged();
      } else {
        show({
          kind: 'failed',
          termsKey: key,
          message: errorMessage(mutationError, 'Something went wrong. Please try again.'),
        });
      }
    } finally {
      draftWriteInFlight.current = false;
    }
    const next = draftQueuedBehindWrite.current;
    draftQueuedBehindWrite.current = undefined;
    if (next && isStillToAutosave(next) && termsKey(next) !== lastSavedTermsKey.current) {
      void writeDraftRef.current(next);
    }
  };
  const writeDraftRef = useRef(writeDraft);
  writeDraftRef.current = writeDraft;

  // A pending draft is written only if it is still what the form would autosave: a series that
  // went final, or a confirmation that opened, since it was queued drops it.
  const flushPendingDraft = () => {
    const terms = pendingDraft.current;
    pendingDraft.current = undefined;
    if (terms && isStillToAutosave(terms)) {
      void writeDraft(terms);
    }
  };
  const flushPendingDraftRef = useRef(flushPendingDraft);
  flushPendingDraftRef.current = flushPendingDraft;

  const currentTermsKey = currentTerms ? termsKey(currentTerms) : undefined;
  const shownDraftStatus =
    draftStatus.kind === 'idle' ||
    draftStatus.termsKey !== currentTermsKey ||
    (isFinal && draftStatus.kind !== 'alreadyFinal')
      ? IDLE
      : draftStatus;

  useEffect(() => {
    if (!autosaveTerms || autosaveKey === lastSavedTermsKey.current) {
      pendingDraft.current = undefined;
      return undefined;
    }
    pendingDraft.current = autosaveTerms;
    const timer = setTimeout(() => flushPendingDraftRef.current(), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // autosaveKey stands for autosaveTerms.
  }, [autosaveKey]);

  // Signing out navigates away, so a draft still waiting on the debounce is sent now.
  useEffect(() => {
    const flush = () => flushPendingDraftRef.current();
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      mounted.current = false;
      flush();
    };
  }, []);

  // Restores the bidder's latest draft once, when the drafts first load, unless they have
  // already started on the form.
  useEffect(() => {
    if (restoreAttempted.current || !draftsSettled) {
      return;
    }
    restoreAttempted.current = true;
    const biddableTerms = (draft: SaleBidDraft): BidTerms =>
      saleAllowsJointVentureBids
        ? draftTerms(draft)
        : { ...draftTerms(draft), structure: SaleBidStructure.CASH };
    const offeredIds = new Set(offerings.map((offering) => offering.offering_id));
    // Submitting deletes only the submitted series' draft, and autosave can't write while the
    // confirmation is open, so a draft saved before the round's latest bid was left behind on the
    // way to that bid, not chosen.
    const latestBidMillis = Math.max(
      -Infinity,
      ...existingBids
        .filter((bid) => bid.round === round)
        .map((bid) => DateTime.fromISO(bid.received_at).toMillis()),
    );
    const latest = (drafts.data?.saleBidDrafts ?? [])
      .filter(
        (draft) =>
          draft.round === round &&
          DateTime.fromISO(draft.saved_at).toMillis() > latestBidMillis &&
          !isSeriesFinal(biddableTerms(draft)) &&
          draft.offering_ids.every((offeringId) => offeredIds.has(offeringId)),
      )
      .sort(
        (a, b) => DateTime.fromISO(b.saved_at).toMillis() - DateTime.fromISO(a.saved_at).toMillis(),
      )[0];
    if (!latest || bidderHasEdited.current) {
      return;
    }
    const restored = biddableTerms(latest);
    setSelectedOfferingIds([...restored.offering_ids]);
    setStructure(restored.structure);
    if (latest.pct_e4 != null) {
      setMode('PCT');
      setRawValue(formatPctE4(latest.pct_e4));
    } else {
      setMode('AMOUNT');
      setRawValue((Number(latest.amount_cents) / 100).toFixed(2));
    }
    if (restored.structure !== latest.structure) {
      // Left unsaved, so autosave writes the restored terms as a Cash draft.
      setJointVentureDraftRestoredAsCash(true);
      return;
    }
    const restoredKey = termsKey(restored);
    lastSavedTermsKey.current = restoredKey;
    setDraftStatus({ kind: 'saved', termsKey: restoredKey, savedAt: latest.saved_at });
    // Runs when the drafts query settles; existingBids, isSeriesFinal, round, offerings and
    // saleAllowsJointVentureBids are read as of then.
  }, [draftsSettled]);

  const holdsSeriesOf = (bids: readonly SaleBid[], terms: BidTerms): boolean =>
    bids.some(
      (bid) =>
        seriesKey(bid.round, bid.structure, bid.offering_ids) ===
        seriesKey(terms.round, terms.structure, terms.offering_ids),
    );

  const validTermsOrToast = (): BidTerms | undefined => {
    if (selectedOfferingIds.length === 0) {
      toast.error('Select at least one pool.');
      return undefined;
    }
    if (!currentTerms) {
      toast.error(mode === 'PCT' ? 'Enter a valid percentage.' : 'Enter a valid dollar amount.');
      return undefined;
    }
    return currentTerms;
  };

  const handleSubmitFinal = () => {
    const terms = validTermsOrToast();
    if (terms) {
      setConfirmTerms(terms);
    }
  };

  // The dialog stays open, locked, until the refetched bids land, whatever the submit's outcome:
  // a timeout or dropped response can follow a bid that loan-service recorded, and the form must
  // not offer Submit, or autosave a draft, for a series that has just gone final.
  const handleConfirmSubmit = async () => {
    const terms = confirmTerms;
    if (!terms) {
      return;
    }
    setFinalizing(true);
    let outcome: 'submitted' | 'alreadyBid' | 'failed' = 'failed';
    let failure: unknown;
    try {
      await submitBid({ variables: { input: toBidInput(saleId, terms) } });
      outcome = 'submitted';
    } catch (mutationError) {
      outcome = isAlreadyBidError(mutationError) ? 'alreadyBid' : 'failed';
      failure = mutationError;
    }
    if (outcome !== 'failed') {
      markSeriesFinal(terms);
    }
    const bids = await onBidsChanged();
    if (!mounted.current) {
      return;
    }
    setFinalizing(false);
    if (outcome === 'alreadyBid') {
      setConfirmTerms(undefined);
      toast.error(ALREADY_BID_COPY);
      return;
    }
    if (outcome === 'submitted' || (bids && holdsSeriesOf(bids, terms))) {
      setConfirmTerms(undefined);
      toast.success('Final bid submitted.');
      return;
    }
    toast.error(errorMessage(failure, 'Failed to submit bid'));
  };

  const editingLocked = finalizing || submitting;

  return (
    <div
      data-testid="sale-bid-form"
      aria-busy={!draftsSettled}
      className="mt-4 rounded border border-slate-500 border-transparent-0 bg-background-canvas p-4 light:border-gray-200 light:bg-white"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          flushPendingDraftRef.current();
        }
      }}
    >
      <div className="text-sm text-white light:text-gray-900">
        Round {round} · Bids due {formatCentralTime(deadlineAt)}
      </div>
      {drafts.error && !drafts.data && (
        <p
          data-testid="sale-bid-draft-load-error"
          className="mt-2 text-xs text-status-error light:text-error-muted"
        >
          Your saved draft couldn&apos;t be loaded. New changes still save.
        </p>
      )}

      <div className="mt-4 text-xs font-bold text-gray-400 light:text-gray-700">
        Pools (select one or more to link)
      </div>
      <div className="mt-2 space-y-1">
        {offerings.map((offering) => (
          <Checkbox
            key={offering.offering_id}
            data-testid={`sale-bid-pool-${offering.offering_id}`}
            checked={selectedOfferingIds.includes(offering.offering_id)}
            disabled={editingLocked}
            onChange={() => toggleOffering(offering.offering_id)}
            label={offering.offering_name}
            supportsLightTheme
          />
        ))}
      </div>

      <RadioGroup label="Bid structure" className="mt-4 flex gap-4">
        <RadioButton
          data-testid="sale-bid-structure-cash"
          checked={structure === SaleBidStructure.CASH}
          disabled={editingLocked}
          onChange={() => {
            markEdited();
            setStructure(SaleBidStructure.CASH);
          }}
          label="Cash"
          size="small"
          supportsLightTheme
        />
        {saleAllowsJointVentureBids && (
          <RadioButton
            data-testid="sale-bid-structure-jvt"
            checked={structure === SaleBidStructure.JVT}
            disabled={editingLocked}
            onChange={() => {
              markEdited();
              setStructure(SaleBidStructure.JVT);
            }}
            label="Joint Venture"
            size="small"
            supportsLightTheme
          />
        )}
      </RadioGroup>
      {jointVentureDraftRestoredAsCash && (
        <p
          data-testid="sale-bid-restored-as-cash"
          className="mt-2 text-xs text-gray-400 light:text-gray-700"
        >
          This sale takes cash bids only, so your saved Joint Venture draft was restored as Cash.
        </p>
      )}

      <label
        htmlFor={amountInputId}
        className="mt-4 block text-xs font-bold text-gray-400 light:text-gray-700"
      >
        {mode === 'PCT' ? 'Bid, percent of Book Value' : 'Bid, dollars'}
      </label>
      <div className="mt-2 flex items-center gap-4">
        <RadioGroup label="Bid unit" className="flex items-center gap-4">
          <RadioButton
            data-testid="sale-bid-unit-pct"
            checked={mode === 'PCT'}
            disabled={editingLocked}
            onChange={() => {
              markEdited();
              setMode('PCT');
            }}
            label="%"
            ariaLabel="Percent"
            size="small"
            supportsLightTheme
          />
          <RadioButton
            data-testid="sale-bid-unit-amount"
            checked={mode === 'AMOUNT'}
            disabled={editingLocked}
            onChange={() => {
              markEdited();
              setMode('AMOUNT');
            }}
            label="$"
            ariaLabel="Dollars"
            size="small"
            supportsLightTheme
          />
        </RadioGroup>
        <input
          id={amountInputId}
          type="number"
          data-testid="sale-bid-amount"
          value={rawValue}
          disabled={editingLocked}
          onChange={(event) => {
            markEdited();
            setRawValue(event.target.value);
          }}
          className="w-48 rounded border border-gray-600 bg-background-surface px-3 py-1.5 text-white outline-none focus:border-accent-default light:bg-white light:text-gray-900 light:focus:border-pink-700"
        />
      </div>
      <p
        data-testid="sale-bid-price-equivalent"
        aria-live="polite"
        className="mt-2 text-xs text-gray-400 light:text-gray-700"
      >
        {priceEquivalent}
      </p>

      {submittedBid && (
        <p
          data-testid="sale-bid-final-note"
          className="mt-2 text-xs text-gray-400 light:text-gray-700"
        >
          Submitted, final: {priceLabel(submittedBid)} at{' '}
          {formatCentralTime(submittedBid.received_at)}
        </p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <Button
          size="small"
          type="primary"
          label="Submit final bid"
          data-testid="sale-submit-final"
          className={isFinal || editingLocked ? '' : PRIMARY_BUTTON_LIGHT_THEME}
          disabled={isFinal || editingLocked}
          onClick={handleSubmitFinal}
        >
          Submit final bid
        </Button>
        <DraftStatusLine status={shownDraftStatus} />
      </div>

      {confirmTerms && (
        <SaleBidConfirmDialog
          terms={confirmTerms}
          offerings={offerings}
          deadlineAt={deadlineAt}
          calculationDate={calculationDate}
          submitting={submitting || finalizing}
          onCancel={() => setConfirmTerms(undefined)}
          onSubmit={handleConfirmSubmit}
        />
      )}
    </div>
  );
};

export const SaleBidding: FC<{
  saleId: string;
  // undefined when no round is both open (deadline_at in the future) and one this bidder's
  // company is invited to -- getInvitedOpenRound in SalePage decides this, not here.
  round: number | undefined;
  // The open round's deadline; set whenever round is.
  deadlineAt: string | undefined;
  calculationDate: string;
  offerings: SaleOffering[];
  isSeller: boolean;
  saleAllowsJointVentureBids: boolean;
  // True only when round is defined and no notice is owed -- i.e. the form should render.
  canBid: boolean;
  // Rendered instead of the bid form when a bidder still owes an acknowledgement (the summary
  // bid notice, at L1) -- the caller decides whether one applies, this component just displays it.
  noticeGate?: ReactNode;
}> = ({
  saleId,
  round,
  deadlineAt,
  calculationDate,
  offerings,
  isSeller,
  saleAllowsJointVentureBids,
  canBid,
  noticeGate,
}) => {
  // Bidders can always see their own bids, whether or not bidding is currently open for them --
  // only sellers skip this (SealedBidsList below uses a separate, sealed-aware query instead).
  const { data, loading, error, refetch } = useQuery<SaleBidsQuery>(SALE_BIDS_QUERY, {
    variables: { saleId },
    errorPolicy: 'all',
    skip: isSeller,
  });

  return (
    <div className="mt-6">
      <h2 className="font-heebo text-lg font-medium text-white light:text-gray-900">
        {isSeller ? 'All bids' : 'Your bids'}
      </h2>
      {isSeller ? (
        <SealedBidsList saleId={saleId} offerings={offerings} />
      ) : loading && !data ? (
        <p className="mt-2 text-sm text-gray-400 light:text-gray-700">Loading your bids…</p>
      ) : error && !data ? (
        // Not showing the form here too -- without the submitted bids there's no way to tell
        // which series are already final, so the form would offer to bid on them again.
        <p className="mt-2 text-sm text-status-error light:text-error-muted">
          Failed to load your bids.
        </p>
      ) : (
        <>
          <div className="text-sm text-gray-400 light:text-gray-700">
            Bids as of {DateTime.now().toFormat('M/d/yyyy h:mm a')}:
          </div>
          {(data?.saleBids ?? []).length === 0 ? (
            <p
              data-testid="sale-own-bids-empty"
              className="mt-2 text-sm text-gray-400 light:text-gray-700"
            >
              You haven't bid yet.
            </p>
          ) : (
            <SaleBidsTable
              saleId={saleId}
              bids={(data?.saleBids ?? []).map((bid) => toTableBid(bid, offerings))}
              linkPools
              showBidder={false}
              tableTestId="sale-own-bids"
              rowTestId="sale-own-bid"
            />
          )}
          {canBid && round != null && deadlineAt != null ? (
            <BidForm
              key={`${saleId}:${round}`}
              saleId={saleId}
              round={round}
              deadlineAt={deadlineAt}
              calculationDate={calculationDate}
              offerings={offerings}
              existingBids={data?.saleBids ?? []}
              saleAllowsJointVentureBids={saleAllowsJointVentureBids}
              onBidsChanged={async () => {
                try {
                  return (await refetch()).data?.saleBids;
                } catch {
                  return undefined;
                }
              }}
            />
          ) : (
            (noticeGate ?? (
              <p
                data-testid="sale-bidding-closed"
                className="mt-2 text-sm text-gray-400 light:text-gray-700"
              >
                Bidding is closed for this sale.
              </p>
            ))
          )}
        </>
      )}
    </div>
  );
};
